import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import AdminLayout from '../layout/AdminLayout';
import { getReviewPermission, getMarketingMetrics, getAllBots, getAdminUsage, setProviderBillingWaive, setProviderOpenAIKeyField, setProviderUsageCap, MarketingRow, ReviewBot, UsageBotRow, UsageProviderRow } from '../../services/providerReviewService';
import './MarketingPage.css';

/**
 * Marketing dashboard: LP impressions → console opens → signups → bots, from
 * the MarketingMetrics daily counters (see provider-api/marketing-metrics.ts
 * for the metric key shapes). Gated on the same reviewEnabled permission as
 * the other marketplace admin pages.
 */

// Series colors validated with the dataviz palette checker (light surface):
// lp_view #2563eb / console_open #c2410c — CVD ΔE 117, contrast ≥3:1.
const SERIES = [
  { key: 'lp_view', color: '#2563eb' },
  { key: 'console_open', color: '#c2410c' },
] as const;

const FUNNEL_EVENTS = ['lp_view', 'console_open', 'signup', 'bot_created'] as const;

function isoDay(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function daysAgo(n: number): string {
  return isoDay(new Date(Date.now() - n * 86400_000));
}

/** Sum counts for rows whose metric matches exactly. */
function totalOf(rows: MarketingRow[], metric: string): number {
  return rows.reduce((s, r) => (r.metric === metric ? s + r.count : s), 0);
}

/** Group rows by the dimension value that follows a metric prefix. */
function breakdown(rows: MarketingRow[], prefix: string): Array<{ dim: string; count: number }> {
  const acc = new Map<string, number>();
  for (const r of rows) {
    if (!r.metric.startsWith(prefix)) continue;
    const dim = r.metric.slice(prefix.length);
    acc.set(dim, (acc.get(dim) || 0) + r.count);
  }
  return Array.from(acc, ([dim, count]) => ({ dim, count })).sort((a, b) => b.count - a.count);
}

const MarketingPage: React.FC = () => {
  const { t } = useTranslation();
  const [allowed, setAllowed] = useState<boolean | null>(null);
  const [from, setFrom] = useState(daysAgo(13));
  const [to, setTo] = useState(daysAgo(0));
  const [rows, setRows] = useState<MarketingRow[]>([]);
  const [bots, setBots] = useState<ReviewBot[]>([]);
  const [usageRows, setUsageRows] = useState<UsageBotRow[]>([]);
  const [usageProviders, setUsageProviders] = useState<UsageProviderRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (f: string, tt: string) => {
    setLoading(true);
    setError(null);
    try {
      setRows(await getMarketingMetrics(f, tt));
      // Bot names for the per-bot section (round-3 item 7); non-fatal.
      getAllBots().then(setBots).catch(() => undefined);
      // Token usage for the per-bot section (billing plan 2026-07-08); non-fatal.
      getAdminUsage().then((u) => { setUsageRows(u.bots); setUsageProviders(u.providers || []); }).catch(() => undefined);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    getReviewPermission().then((ok) => {
      setAllowed(ok);
      if (ok) load(from, to);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load]);

  // Per-day totals for the two impression series.
  const dailySeries = useMemo(() => {
    const days: string[] = [];
    for (let ts = Date.parse(`${from}T00:00:00Z`); ts <= Date.parse(`${to}T00:00:00Z`); ts += 86400_000) {
      days.push(isoDay(new Date(ts)));
    }
    const byDay = new Map(days.map((d) => [d, { lp_view: 0, console_open: 0 }]));
    for (const r of rows) {
      const slot = byDay.get(r.day);
      if (!slot) continue;
      if (r.metric === 'total#lp_view') slot.lp_view += r.count;
      if (r.metric === 'total#console_open') slot.console_open += r.count;
    }
    return days.map((d) => ({ day: d, ...byDay.get(d)! }));
  }, [rows, from, to]);

  const maxDaily = Math.max(1, ...dailySeries.flatMap((d) => [d.lp_view, d.console_open]));
  const funnel = FUNNEL_EVENTS.map((ev) => ({ event: ev, count: totalOf(rows, `total#${ev}`) }));
  const pages = breakdown(rows, 'page#lp_view#');
  const sources = breakdown(rows, 'source#lp_view#');
  const contents = breakdown(rows, 'content#lp_view#');
  const signupEntries = breakdown(rows, 'entry#signup#');
  // Round-3 item 7: per-bot access (expert page views) and interactions (messages).
  const botViews = new Map(breakdown(rows, 'bot#view#').map((r) => [r.dim, r.count]));
  const botMsgs = new Map(breakdown(rows, 'bot#msg#').map((r) => [r.dim, r.count]));
  const botStats = Array.from(new Set(Array.from(botViews.keys()).concat(Array.from(botMsgs.keys()))))
    .map((botId) => {
      const bot = bots.find((b) => b.botId === botId);
      const usage = usageRows.find((r) => r.botId === botId);
      return {
        botId,
        name: bot ? bot.displayName : botId.slice(0, 8),
        status: bot?.status,
        views: botViews.get(botId) || 0,
        msgs: botMsgs.get(botId) || 0,
        tokens: usage?.totalTokens || 0,
        rawUsd: usage?.rawUsd || 0,
      };
    })
    .sort((a, b) => (b.views + b.msgs) - (a.views + a.msgs));

  if (allowed === null) return <AdminLayout><div className="marketing-page" /></AdminLayout>;
  if (!allowed) {
    return (
      <AdminLayout>
        <div className="marketing-page"><p className="marketing-error">{t('marketing.noPermission', 'You do not have permission to view marketing metrics.')}</p></div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="marketing-page">
        <div className="marketing-head">
          <div>
            <h2>{t('marketing.title', 'Marketing')}</h2>
            <p className="marketing-subtitle">{t('marketing.subtitle', 'Landing-page impressions and the signup funnel, by day (UTC).')}</p>
          </div>
          <div className="marketing-range">
            <label>
              {t('marketing.from', 'From')}
              <input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} />
            </label>
            <label>
              {t('marketing.to', 'To')}
              <input type="date" value={to} min={from} max={daysAgo(0)} onChange={(e) => setTo(e.target.value)} />
            </label>
            <button onClick={() => load(from, to)} disabled={loading}>
              {loading ? t('marketing.loading', 'Loading…') : t('marketing.apply', 'Apply')}
            </button>
          </div>
        </div>

        {error && <p className="marketing-error">{error}</p>}

        {/* Funnel stat tiles: each step + conversion from the previous one. */}
        <div className="marketing-tiles">
          {funnel.map((step, i) => {
            const prev = i > 0 ? funnel[i - 1].count : 0;
            const rate = i > 0 && prev > 0 ? `${((step.count / prev) * 100).toFixed(1)}%` : null;
            return (
              <div className="marketing-tile" key={step.event}>
                <span className="tile-label">{t(`marketing.event.${step.event}`, step.event)}</span>
                <span className="tile-value">{step.count.toLocaleString()}</span>
                {rate && <span className="tile-rate">{t('marketing.ofPrevious', '{{rate}} of previous step', { rate })}</span>}
              </div>
            );
          })}
        </div>

        {/* Daily grouped bars — 2 series, legend + hover tooltips, table below. */}
        <section className="marketing-card">
          <div className="marketing-card-head">
            <h3>{t('marketing.daily', 'Daily impressions')}</h3>
            <div className="marketing-legend">
              {SERIES.map((s) => (
                <span key={s.key} className="legend-item">
                  <span className="legend-swatch" style={{ background: s.color }} />
                  {t(`marketing.event.${s.key}`, s.key)}
                </span>
              ))}
            </div>
          </div>
          <div className="marketing-chart" role="img" aria-label={t('marketing.dailyAria', 'Bar chart of daily landing-page views and console opens')}>
            {dailySeries.map((d) => (
              <div className="chart-day" key={d.day}>
                <div className="chart-bars">
                  {SERIES.map((s) => (
                    <div
                      key={s.key}
                      className="chart-bar"
                      style={{ height: `${(d[s.key] / maxDaily) * 100}%`, background: s.color }}
                      title={`${d.day} · ${t(`marketing.event.${s.key}`, s.key)}: ${d[s.key]}`}
                    />
                  ))}
                </div>
                <span className="chart-day-label">{d.day.slice(5)}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Per-bot access & interaction (round-3 item 7) */}
        <section className="marketing-card">
          <h3>{t('marketing.byBot', 'Access & interaction by bot')}</h3>
          {botStats.length === 0 ? (
            <p className="marketing-empty">{t('marketing.noData', 'No data in this range.')}</p>
          ) : (
            <table className="marketing-table">
              <thead>
                <tr>
                  <th>{t('marketing.bot', 'Bot')}</th>
                  <th className="num">{t('marketing.botViews', 'Page views')}</th>
                  <th className="num">{t('marketing.botMsgs', 'Messages')}</th>
                  <th className="num">{t('marketing.botEngagement', 'Msgs / view')}</th>
                  <th className="num">{t('marketing.botTokens', 'Tokens (MTD)')}</th>
                  <th className="num">{t('marketing.botCost', 'AI cost $ (MTD)')}</th>
                </tr>
              </thead>
              <tbody>
                {botStats.map((r) => (
                  <tr key={r.botId}>
                    <td>{r.name}{r.status ? ` · ${r.status}` : ''}</td>
                    <td className="num">{r.views.toLocaleString()}</td>
                    <td className="num">{r.msgs.toLocaleString()}</td>
                    <td className="num">{r.views > 0 ? (r.msgs / r.views).toFixed(1) : '—'}</td>
                    <td className="num">{r.tokens.toLocaleString()}</td>
                    <td className="num">{r.rawUsd > 0 ? `$${r.rawUsd.toFixed(3)}` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        {/* Per-provider billing overview (round-5): computed estimate + the
            biller's recorded outcome side by side. */}
        <section className="marketing-card">
          <h3>{t('marketing.byProvider', 'Estimated billing by provider (MTD)')}</h3>
          {usageProviders.length === 0 ? (
            <p className="marketing-empty">{t('marketing.noData', 'No data in this range.')}</p>
          ) : (
            <table className="marketing-table">
              <thead>
                <tr>
                  <th>{t('marketing.provider', 'Provider')}</th>
                  <th>{t('marketing.provPlan', 'Plan')}</th>
                  <th className="num">{t('marketing.provPublished', 'Published bots')}</th>
                  <th className="num">{t('marketing.botTokens', 'Tokens (MTD)')}</th>
                  <th className="num">{t('marketing.provRaw', 'AI cost $')}</th>
                  <th className="num">{t('marketing.provEst', 'Est. charge $')}</th>
                  <th>{t('marketing.provInvoice', 'Billing record')}</th>
                  <th className="num">{t('marketing.provCap', 'Cycle spend / cap')}</th>
                </tr>
              </thead>
              <tbody>
                {usageProviders.map((p) => (
                  <ProviderBillingRow key={p.providerId} row={p} onSaved={() => load(from, to)} />
                ))}
              </tbody>
            </table>
          )}
        </section>

        <div className="marketing-grid">
          <BreakdownCard title={t('marketing.byPage', 'LP views by page')} rows={pages} dimLabel={t('marketing.page', 'Page (lang)')} />
          <BreakdownCard title={t('marketing.bySource', 'LP views by source')} rows={sources} dimLabel={t('marketing.source', 'utm_source')} />
          <BreakdownCard title={t('marketing.byContent', 'LP views by post')} rows={contents} dimLabel={t('marketing.content', 'utm_content')} />
          <BreakdownCard title={t('marketing.signupsByEntry', 'Signups by entry point')} rows={signupEntries} dimLabel={t('marketing.entry', 'Entry')} />
        </div>
      </div>
    </AdminLayout>
  );
};

/**
 * One provider row in the billing table + an expandable editor for the
 * negotiated flat plan (fee per Published bot, included AI-cost allowance,
 * per-provider trial switch). Flat-plan providers are charged for real even
 * while global usage billing stays dry-run — the plan is the opt-in.
 */
const ProviderBillingRow: React.FC<{ row: UsageProviderRow; onSaved: () => void }> = ({ row, onSaved }) => {
  const { t } = useTranslation();
  const b = row.billing;
  const [editing, setEditing] = useState(false);
  // One plan for everyone (2026-08-10): the per-provider plan/fee/credit editor
  // is gone. What remains is the waive switch (a month recorded, not charged)
  // and the per-provider OpenAI key FIELD name (never the key itself).
  const [trial, setTrial] = useState(!!b?.trialActive);
  const [keyField, setKeyField] = useState(row.openaiKeyField || '');
  const [capOverride, setCapOverride] = useState(row.usageCapUsd != null ? String(row.usageCapUsd) : '');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = async () => {
    setSaving(true);
    setErr(null);
    try {
      // Validate EVERYTHING before the first network call — the saves run
      // sequentially, and a late validation throw would leave a partial save.
      const capValue = capOverride.trim();
      const prevCap = row.usageCapUsd != null ? String(row.usageCapUsd) : '';
      const capChanged = capValue !== prevCap;
      if (capChanged && capValue !== '' && (!Number.isFinite(Number(capValue)) || Number(capValue) <= 0 || Number(capValue) > 100000)) {
        throw new Error('usage cap must be a positive number up to 100000');
      }
      if (trial !== !!b?.trialActive) await setProviderBillingWaive(row.providerId, trial);
      if ((keyField.trim() || null) !== (row.openaiKeyField || null)) {
        await setProviderOpenAIKeyField(row.providerId, keyField.trim() || null);
      }
      if (capChanged) {
        await setProviderUsageCap(row.providerId, capValue === '' ? null : Number(capValue));
      }
      setEditing(false);
      onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  // Fee is platform-wide now, so the label states the provider's own currency
  // amount plus whichever exemption applies (admin waive / free trial).
  const feeLabel = b?.feeMinorPerBot
    ? (b.flatCurrency === 'jpy' ? '¥' + Number(b.feeMinorPerBot).toLocaleString() : '$' + (Number(b.feeMinorPerBot) / 100).toLocaleString()) + '/bot'
    : '—';
  const planLabel = `${feeLabel}${b?.trialActive ? ` · ${t('marketing.planWaived', 'WAIVED')}` : ''}${b?.inFreeTrial ? ` · ${t('marketing.planTrial', 'TRIAL')}` : ''}`;

  return (
    <>
      <tr>
        <td title={row.providerId}>{row.email || `${row.providerId.slice(0, 8)}…`}</td>
        <td>
          {planLabel}
          {row.openaiKeyField ? <span title={row.openaiKeyField}> · {t('marketing.ownKey', 'own key')}</span> : null}{' '}
          <button type="button" onClick={() => setEditing((v) => !v)} disabled={saving}>
            {editing ? t('marketing.planCancel', 'Cancel') : t('marketing.planEdit', 'Edit')}
          </button>
        </td>
        <td className="num">{row.publishedBots ?? '—'}</td>
        <td className="num">{row.totalTokens.toLocaleString()}</td>
        <td className="num">${row.rawUsd.toFixed(3)}</td>
        <td className="num">${row.estChargeUsd.toFixed(3)}</td>
        <td>
          {row.invoice
            ? `${row.invoice.status}${row.invoice.amountMinor ? ` · ${row.invoice.currency === 'jpy' ? '¥' + Number(row.invoice.amountMinor).toLocaleString() : '$' + (Number(row.invoice.amountMinor) / 100).toFixed(2)}` : ''}`
            : t('marketing.provNoRecord', '— (not yet billed)')}
        </td>
        <td className="num" title={row.cap ? `${row.cap.cycleStart} → ${row.cap.cycleEnd}${row.cap.usageCapUsd != null ? ` · override $${row.cap.usageCapUsd}` : ''}` : undefined}
          style={row.cap && row.cap.spendUsd >= row.cap.capUsd ? { color: '#d64545', fontWeight: 600 } : undefined}>
          {row.cap
            ? `$${row.cap.spendUsd.toFixed(2)} / $${Math.round(row.cap.capUsd)}${row.cap.proven ? '' : ' (T1)'}`
            : row.usageCapUsd != null ? `— (override $${row.usageCapUsd})` : '—'}
        </td>
      </tr>
      {editing && (
        <tr>
          <td colSpan={8}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', padding: '4px 0' }}>
              <label>
                <input type="checkbox" checked={trial} onChange={(e) => setTrial(e.target.checked)} />{' '}
                {t('marketing.planWaiveToggle', 'Waive charges (month recorded, not charged)')}
              </label>
              <label title={t('marketing.capHint', 'Per-provider override of the proven-payment hard cap, raw USD per billing cycle. Empty = platform default ($300). No effect while payment is unproven (Tier 1 stays at the credit).')}>
                {t('marketing.capLabel', 'Usage cap $ (proven)')}{' '}
                <input type="number" min={1} style={{ width: 110 }} placeholder="300" value={capOverride} onChange={(e) => setCapOverride(e.target.value)} />
              </label>
              <label title={t('marketing.keyFieldHint', 'Secret field name in Linebot_configuration holding this provider’s OpenAI key. Add the key to the secret first; applies to NEW bots. Empty = platform key.')}>
                {t('marketing.keyFieldLabel', 'OpenAI key field')}{' '}
                <input type="text" style={{ width: 220 }} placeholder="platform" value={keyField} onChange={(e) => setKeyField(e.target.value)} />
              </label>
              <button type="button" onClick={save} disabled={saving}>
                {saving ? t('marketing.planSaving', 'Saving…') : t('marketing.planSave', 'Save')}
              </button>
              {err && <span className="marketing-error">{err}</span>}
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

const BreakdownCard: React.FC<{ title: string; rows: Array<{ dim: string; count: number }>; dimLabel: string }> = ({ title, rows, dimLabel }) => {
  const { t } = useTranslation();
  const total = Math.max(1, ...rows.map((r) => r.count));
  return (
    <section className="marketing-card">
      <h3>{title}</h3>
      {rows.length === 0 ? (
        <p className="marketing-empty">{t('marketing.noData', 'No data in this range.')}</p>
      ) : (
        <table className="marketing-table">
          <thead>
            <tr><th>{dimLabel}</th><th className="num">{t('marketing.count', 'Count')}</th><th className="bar-col" aria-hidden="true" /></tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.dim}>
                <td>{r.dim.replaceAll('#', ' · ')}</td>
                <td className="num">{r.count.toLocaleString()}</td>
                <td className="bar-col"><div className="row-bar" style={{ width: `${(r.count / total) * 100}%` }} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
};

export default MarketingPage;
